import { useState } from "react";

import { toFormData } from "@king-ict/eupisi-ui/utils";
import { useSelector, useDispatch } from "react-redux";

import {
  createChildDocument,
  createFromExistingChildDocument,
  deleteChildDocument,
  getChildDocument,
} from "../../../api/kindergartenApi";
import Child from "../../../models/Person/Child";
import DocumentMethodsReturnType, {
  DocumentDownloadRemoveMethodReturnType,
} from "../../../models/Request/DocumentMethodsReturnType";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import UploadDocument from "../../../models/Request/UploadDocument";
import {
  selectMainInfo,
  updateRequestChildDocuments,
} from "../../../slices/requestSlice";

interface Props {
  setChild: (prev: (prevData: Child) => Child) => void;
}

export function useChildDocuments(): DocumentDownloadRemoveMethodReturnType;
export function useChildDocuments(props: Props): DocumentMethodsReturnType;
export function useChildDocuments(props?: Props) {
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const requestMainInfoState = useSelector(selectMainInfo);

  const dispatch = useDispatch();
  const handleDownloadDocument = (document: RequestDocumentData) => {
    if (document.fileID) {
      getChildDocument(document.fileID);
    }
  };

  const handleRemoveDocuments = (documents: RequestDocumentData[]) => {
    let ids = new Array<number>();
    documents.forEach((document) => {
      if (document.id) {
        ids.push(document.id);
      }
    });

    if (ids.length > 0) {
      deleteChildDocument(ids);
    }
  };

  if (props) {
    const { setChild } = props;

    const handleCloseDocumentsModal = (documents: RequestDocumentData[]) => {
      handleRemoveDocuments(documents);

      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleConfirmDocumentsModal = (documents: RequestDocumentData[]) => {
      setChild((prev) => {
        const newDocuments = prev.documents
          ? [...prev.documents, ...documents]
          : [...documents];

        return { ...prev, documents: newDocuments };
      });

      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleUploadDocuments = async (files: File[]) => {
      var documents = await createChildDocument(
        toFormData<UploadDocument>({
          requestUID: requestMainInfoState.uId,
          applicantParentOib: requestMainInfoState.applicantParentOib,
          childOib: requestMainInfoState.childOib,
          files: files,
        })
      );
      return documents;
    };

    const handleRemoveDocument = (document: RequestDocumentData) => {
      if (document.id) {
        const ids = [document.id];
        deleteChildDocument(ids);
      }
    };

    const handleRemoveDocumentFromList = (document: RequestDocumentData) => {
      setChild((prev) => {
        if (prev.documents) {
          const newDocuments = prev.documents.filter(
            (d) => d.id !== document.id
          );

          dispatch(updateRequestChildDocuments(newDocuments));
          return { ...prev, documents: newDocuments };
        }
        return prev;
      });

      handleRemoveDocument(document);
    };

    const handleUploadExistingDocument = async (
      document: RequestDocumentData
    ) => {
      var docResult = await createFromExistingChildDocument(
        document.fileID!,
        document.origin
      );
      return docResult;
    };

    const handleToggleDocumentsModal = () => {
      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    return {
      isDocumentsModalOpen,
      handleUploadDocuments,
      handleUploadExistingDocument,
      handleConfirmDocumentsModal,
      handleCloseDocumentsModal,
      handleToggleDocumentsModal,
      handleDownloadDocument,
      handleRemoveDocumentFromList,
      handleRemoveDocument,
      handleRemoveDocuments,
    };
  } else {
    return {
      handleDownloadDocument,
      handleRemoveDocuments,
    };
  }
}
